import type { MetaMatches } from '@remix-run/react/dist/routeModules'
import { User } from '@sentry/node'
import { diff } from 'deep-object-diff'
import { get, omit, set } from 'lodash-es'
import { ListingBroker, Organization } from '~/models'
import { PROPERTY_TYPES_COLORS } from './constants'

export const getPaths = (obj: any, prev = ''): string[] => {
  const result = []
  for (let k in obj) {
    let path = prev + (prev ? '.' : '') + k
    if (obj[k] && typeof obj[k].sub_type !== 'undefined') {
      result.push(path)
      continue
    } else if (typeof obj[k] == 'object') {
      result.push(...getPaths(obj[k], path))
    } else {
      result.push(path)
    }
  }
  return result
}

export function valuesToValueLabelOptions(
  values: { [key: string]: any } | string[]
) {
  const options: { value: string; label: string }[] = []
  if (Array.isArray(values)) {
    values.forEach((value) => {
      options.push({ value, label: value })
    })
  } else {
    Object.keys(values).forEach((value) => {
      const label = values[value]
      options.push({ value, label })
    })
  }
  return options
}

export function debounce<F extends (...args: Parameters<F>) => ReturnType<F>>(
  fn: F,
  ms = 300
) {
  let timeoutId: ReturnType<typeof setTimeout>
  return function (this: any, ...args: Parameters<F>) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn.apply(this, args), ms)
  }
}

export function slugify(
  ...args: (string | number | undefined | null)[]
): string {
  const value = args.join(' ')

  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/\s+|\//g, '-')
}

export function getPropertyTypeClassName(type: string) {
  let bdgColor = PROPERTY_TYPES_COLORS.find(
    (config) => config.propertyType === type
  )
  return bdgColor?.className
}

export function tryParseJson(value: string) {
  try {
    return JSON.parse(value)
  } catch (error) {
    //
  }
  return []
}

export function transformImageUrl(url: string) {
  // const extension = url.includes('images') ? '.jpg' : '.png'
  const extension = '.png'
  return url
    .replace(
      'https://storage.googleapis.com/resimplifi-associate',
      'https://us-central1-resimplifi-1566929291680.cloudfunctions.net/associate-transform-image'
      // 'http://localhost:1207'
    )
    .replace('.webp', extension)
}

export function getMaxSubstringLength(value: string, maxLength: number) {
  if (value.length <= maxLength) return value.length
  const lastSpaceIndex = value.lastIndexOf(' ', maxLength)
  if (lastSpaceIndex === -1) return maxLength
  return lastSpaceIndex
}

export function getBrokerMemberships(broker: ListingBroker | User) {
  return (
    broker.meta?.memberships
      ?.split(/ |,|\|/g)
      .filter((v: string) => v)
      .join(', ') || ''
  )
}

export function getRoutMeta({
  matches,
  title,
  description,
  image,
}: {
  matches: MetaMatches
  title: string
  description?: string
  image?: string
}) {
  const rootData = matches.find((match) => match.id === 'root')!.data as {
    organization: Organization
  }

  const meta = [
    { title: title },
    {
      property: 'og:title',
      content: title,
    },
    {
      name: 'twitter:title',
      content: title,
    },

    {
      name: 'twitter:creator',
      content: '@resimplifiinc',
    },

    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      name: 'twitter:site',
      content: '@resimplifiinc',
    },
  ]
  if (description) {
    const cleanedDescription = description
      .replace(/<[^>]*>?/gm, '')
      .split('\n')
      .filter((v) => v)
      .join(' ')
      .substring(0, getMaxSubstringLength(description || '', 155))

    const descriptionValue =
      cleanedDescription.length < description.length
        ? `${cleanedDescription}...`
        : cleanedDescription

    meta.push(
      ...[
        {
          name: 'description',
          content: descriptionValue,
        },
        {
          property: 'og:description',
          content: descriptionValue,
        },
        {
          name: 'twitter:description',
          content: descriptionValue,
        },
      ]
    )
  } else {
    // Default organization description?
  }
  if (image) {
    meta.push(
      ...[
        {
          property: 'og:image',
          content: image,
        },
        {
          name: 'twitter:image',
          content: image,
        },
      ]
    )
  } else {
    meta.push(
      ...[
        {
          property: 'og:image',
          content: rootData.organization?.meta.share
            ? rootData.organization.meta.share.url
            : '/open-graph.png',
        },
        {
          name: 'twitter:image',
          content: rootData.organization?.meta.share
            ? rootData.organization.meta.share.url
            : '/open-graph.png',
        },
      ]
    )
  }
  return meta
}

export function merge<T extends object>(
  target: T,
  ...sources: Array<Partial<T>>
): T {
  const merged: T = { ...target }

  for (const source of sources) {
    for (const key in source) {
      if (source[key] !== undefined) {
        if (
          typeof source[key] === 'object' &&
          source[key] !== null &&
          typeof merged[key] === 'object' &&
          merged[key] !== null
        ) {
          merged[key] = merge(merged[key], source[key])
        } else {
          merged[key as keyof T] = source[key] as T[keyof T]
        }
      }
    }
  }

  return merged
}

export function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  if (chunkSize <= 0) {
    throw new Error('chunk size must be greater than zero')
  }

  const chunked: T[][] = []

  for (let i = 0; i < array.length; i += chunkSize) {
    chunked.push(array.slice(i, i + chunkSize))
  }

  return chunked
}

export function shuffle<T>(array: T[]) {
  let i = array.length
  let j = 0
  let temp: T

  while (--i > 0) {
    j = Math.floor(Math.random() * (i + 1))
    temp = array[j]
    array[j] = array[i]
    array[i] = temp
  }
}

export function getJsonCompatObject(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj
  }

  if (obj instanceof Date) {
    return obj.toISOString()
  } else if (Array.isArray(obj)) {
    return obj.map(getJsonCompatObject)
  } else if (
    typeof obj === 'object' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      obj.toString()
    )
  ) {
    return obj.toString()
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const result = {}
    Object.keys(obj).forEach((key) => {
      const value = get(obj, key)
      set(result, key, getJsonCompatObject(value))
    })
    return result
  }

  return obj
}

export function getDaysBetweenDates(first: Date, second: Date): number {
  const firstTime = first.getTime()
  const secondTime = second.getTime()
  const difference = firstTime - secondTime
  const days = Math.floor(difference / 86_400_000)

  return days
}

const ignoreKeys = [
  'organization[_id]',
  'organization[slug]',
  'organization[name]',
  'organization',
  'tid',
  '_id',
  'slug',
  'modified',
  'offMarketDate', // set to null in prepare listing, but shows as undefined on form
  'activeDate',
  'property.placeId',
  'property.country',
  'location',
]

export function getObjectChanges<T extends object>(
  original: T,
  updated: T
): Partial<T> {
  const changes = diff(
    omit({ ...original }, ignoreKeys),
    omit({ ...updated }, ignoreKeys)
  )

  return changes
}

const dateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?)?$/

export function convertDateValues<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return obj
  }

  if (obj instanceof Date) {
    return obj
  }

  if (typeof obj === 'string') {
    if (dateRegex.test(obj)) {
      return new Date(obj) as T
    }

    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map(convertDateValues) as T
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const result: any = {}

    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = convertDateValues(obj[key])
      }
    }

    return result as T
  }

  return obj
}
